import { useEffect, useState, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";

/** Polls /notifications for the current employee every 4s and shows
 *  an incoming handover-request Alert dialog. */
export default function NotificationPoller() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [decision, setDecision] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: false } });
      // Newest unhandled handover_request wins
      const req = data.find((n) => n.type === "handover_request" && !n.handled);
      setPending((prev) => (prev?.id === req?.id ? prev : req || null));
      // Toast for reject/accept/handover_started results (once)
      for (const n of data) {
        if (!n.read && ["handover_rejected", "handover_accepted", "handover_started"].includes(n.type)) {
          const icon = n.type === "handover_rejected" ? "❌" : "✅";
          toast(`${icon} ${n.title}`, { description: n.body });
          await api.post(`/notifications/${n.id}/read`).catch(() => {});
        }
      }
    } catch {
      /* silent — polling error */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, 4000);
    return () => clearInterval(id);
  }, [user, fetchNotifs]);

  const decide = async (accept) => {
    if (!pending) return;
    setDecision(true);
    try {
      await api.post(`/notifications/${pending.id}/decide`, { accept });
      toast.success(accept ? "Handover accepted" : "Handover rejected");
      setPending(null);
    } catch (e) { toast.error(formatError(e)); }
    finally { setDecision(false); }
  };

  if (!pending) return null;
  const d = pending.data || {};

  return (
    <AlertDialog open={!!pending} onOpenChange={(v) => !v && !decision && setPending(null)}>
      <AlertDialogContent data-testid="incoming-handover-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-xl">New shift assignment</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <Chip label="MPD" value={d.mpd} />
                <Chip label="Business date" value={d.business_date} />
                <Chip label="Current employee" value={d.from_name} />
                <Chip label="Requested by" value={pending.title.split("—")[1]?.trim() || d.mpd} />
              </div>
              <div className="text-sm text-slate-600">{pending.body}</div>
              <div className="text-sm font-medium">Accept this shift?</div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={(e) => { e.preventDefault(); decide(false); }}
            disabled={decision}
            data-testid="handover-reject-btn"
            className="rounded-xl"
          >
            <UserX className="w-4 h-4 mr-2" /> Reject
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); decide(true); }}
            disabled={decision}
            data-testid="handover-accept-btn"
            className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"
          >
            <UserCheck className="w-4 h-4 mr-2" /> Accept
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Chip({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-100 p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 truncate">{value || "—"}</div>
    </div>
  );
}
